import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  TrendingUp, 
  FileSpreadsheet, 
  PieChart, 
  CheckCircle2, 
  ArrowRight,
  Star,
  Shield,
  Zap,
  BarChart3,
  DollarSign,
  Calendar,
  Users,
  Download,
  Target,
  Clock,
  ChevronRight,
  Play,
  Sparkles,
  LineChart,
  Building,
  Landmark,
  Briefcase,
  UserCheck,
  Check
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // Set SEO-friendly page title
  useEffect(() => {
    document.title = "Hotel Budget Template - Free Hotel Financial Planning Tool | HotelBudget Australia";
    
    // Update meta description dynamically
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Free hotel budget template for Australian hotels & motels. Create professional hotel budgets with P&L reports, cash flow forecasting & 5-year projections. Download Excel spreadsheet instantly.');
    }
  }, []);

  // Auto-rotate steps for animation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stakeholders = [
    {
      icon: <Building className="w-6 h-6" />,
      title: "Managers & Operators",
      benefits: ["Improve profitability", "Control costs effectively", "Make faster decisions", "Track KPIs in real-time"]
    },
    {
      icon: <UserCheck className="w-6 h-6" />,
      title: "Owners & Investors",
      benefits: ["Evaluate performance", "Compare assets easily", "Maximise returns", "Professional reporting"]
    },
    {
      icon: <Landmark className="w-6 h-6" />,
      title: "Bankers & Lenders",
      benefits: ["Standardised forecasts", "Reduce approval time", "Improve risk assessment", "Transparent data"]
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Mortgage Agents",
      benefits: ["Client-ready reports", "Faster loan processing", "Credible projections", "Professional format"]
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Add Your Property",
      description: "Enter your hotel details - rooms, location, and property type.",
      preview: "property"
    },
    {
      number: "02", 
      title: "Set Occupancy & Rates",
      description: "Input monthly occupancy rates and average daily rates (ADR).",
      preview: "occupancy"
    },
    {
      number: "03",
      title: "Customize Expenses",
      description: "Use industry benchmarks or customize your expense drivers.",
      preview: "expenses"
    },
    {
      number: "04",
      title: "Download Reports",
      description: "Get P&L, cash flow, and 5-year projections instantly.",
      preview: "reports"
    }
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "One-Click Budgets",
      description: "Generate complete hotel budgets in seconds with industry-standard benchmarks.",
      gradient: "from-[#4B5E40] to-[#5A7A5A]",
      size: "lg"
    },
    {
      icon: <LineChart className="w-7 h-7" />,
      title: "5-Year Forecasting",
      description: "Plan with confidence using detailed cash flow projections.",
      gradient: "from-[#C9A227] to-[#D4B030]",
      size: "sm"
    },
    {
      icon: <Target className="w-7 h-7" />,
      title: "Budget vs Actuals",
      description: "Track performance and identify cost leaks instantly.",
      gradient: "from-amber-500 to-orange-500",
      size: "md"
    },
    {
      icon: <BarChart3 className="w-7 h-7" />,
      title: "Industry Benchmarks",
      description: "Built-in hospitality metrics for credible, lender-ready reports.",
      gradient: "from-emerald-500 to-green-500",
      size: "sm"
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#4B5E40] to-[#8FA385] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                HotelBudget
              </span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button 
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              >
                How It Works
              </button>
              <button 
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              >
                Pricing
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-900 font-medium">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white font-semibold px-6 rounded-full shadow-lg shadow-[#C8D4C0]">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-[#FAF9F6]">
        {/* Background decorations */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-[#C8D4C0]/30 to-[#D4C9A0]/30 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#C9A227]/20 to-[#E8C547]/20 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-left">
              <Badge className="mb-6 bg-[#E8EDE5] text-[#3C4C33] hover:bg-[#E8EDE5] px-4 py-2 text-sm font-semibold">
                <Sparkles className="w-4 h-4 mr-2" />
                #1 Hotel Budget Template in Australia
              </Badge>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Free Hotel Budget
                <br />
                <span className="bg-gradient-to-r from-[#C9A227] via-[#D4B030] to-[#E8C547] bg-clip-text text-transparent">
                  Template & Planning
                </span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
                Create professional hotel budgets, P&L reports, and cash flow forecasts in seconds. The easiest hotel budget template with automatic calculations and Excel download.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white text-lg px-8 py-6 rounded-full shadow-xl shadow-[#C8D4C0] font-semibold"
                  >
                    Get Free Hotel Budget Template
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="text-lg px-8 py-6 rounded-full border-2 border-slate-200 hover:border-[#A5B89A] font-medium"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                >
                  <Play className="w-5 h-5 mr-2 text-[#4B5E40]" />
                  See How It Works
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 text-slate-500 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span>Free hotel budget template</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span>Bank-ready Excel reports</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span>Instant download</span>
                </div>
              </div>
            </div>

            {/* Right - Dashboard Preview */}
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl shadow-2xl p-6 transform lg:rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="bg-slate-800/80 rounded-2xl p-5">
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    {[
                      { label: "Annual Revenue", value: "$2.4M", icon: <DollarSign className="w-4 h-4" /> },
                      { label: "Occupancy", value: "72%", icon: <TrendingUp className="w-4 h-4" /> },
                      { label: "RevPAR", value: "$142", icon: <BarChart3 className="w-4 h-4" /> },
                      { label: "Net Profit", value: "$380K", icon: <Target className="w-4 h-4" /> }
                    ].map((stat, idx) => (
                      <div key={idx} className="bg-slate-700/60 rounded-xl p-3 backdrop-blur">
                        <div className="flex items-center gap-2 text-slate-400 mb-1">
                          {stat.icon}
                          <span className="text-xs">{stat.label}</span>
                        </div>
                        <p className="text-xl font-bold text-white">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Chart Preview */}
                  <div className="h-40 bg-gradient-to-t from-[#C9A227]/20 to-transparent rounded-xl flex items-end p-3 gap-1.5">
                    {[45, 52, 48, 65, 72, 68, 75, 82, 78, 85, 90, 88].map((h, i) => (
                      <div 
                        key={i} 
                        className="flex-1 bg-gradient-to-t from-[#C9A227] to-[#E8C547] rounded-t-lg transition-all duration-500 hover:from-[#D4B030] hover:to-[#F0D060]"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 transform -rotate-3 hover:rotate-0 transition-transform">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Profit Increased</p>
                    <p className="text-emerald-600 font-bold">+24% this year</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built For Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-500 font-medium mb-8">BUILT FOR EVERY HOSPITALITY STAKEHOLDER</p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {["Hotel Owners", "Property Managers", "Investors", "Asset Managers", "Mortgage Agents", "Bankers"].map((role, idx) => (
              <span key={idx} className="text-lg md:text-xl font-semibold text-slate-700">{role}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Bento Grid */}
      <section id="features" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#F5EDCE] text-[#8B7355] hover:bg-[#F5EDCE]">
              POWERFUL FEATURES
            </Badge>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              Everything You Need to
              <br />
              <span className="bg-gradient-to-r from-[#4B5E40] to-[#8FA385] bg-clip-text text-transparent">
                Budget Like a Pro
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Unlike generic accounting software, our platform is built exclusively for hotels with real hospitality industry benchmarks.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Large Feature Card */}
            <div className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-[#4B5E40] to-[#3C4C33] rounded-3xl p-8 text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 backdrop-blur">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-4">One-Click Budget Creation</h3>
                <p className="text-white/80 text-lg mb-6">
                  Stop spending weeks in spreadsheets. Generate accurate, bank-ready hotel budgets instantly with our auto-filled industry benchmarks, smart revenue modelling, and customisable assumptions.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Auto-filled benchmarks</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Smart modelling</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Professional format</span>
                </div>
              </div>
            </div>

            {/* Medium cards */}
            {features.slice(1).map((feature, idx) => (
              <div 
                key={idx} 
                className={`bg-white border border-slate-200 rounded-3xl p-6 hover:shadow-xl hover:border-[#C8D4C0] transition-all duration-300 group ${idx === 1 ? 'md:row-span-2' : ''}`}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}

            {/* Professional Reports Card */}
            <div className="md:col-span-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-3xl p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                  <FileSpreadsheet className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Professional Reports — Ready to Download</h3>
                  <p className="text-slate-600 mb-4">Generate clean, branded financial reports in seconds. Perfect for bank submissions, investor presentations, and board meetings.</p>
                  <div className="flex flex-wrap gap-2">
                    {["Operating Budgets", "Cash Flow Statements", "P&L Forecasts", "Investment Summaries"].map((item, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-amber-200">{item}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stakeholders Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              FOR EVERY STAKEHOLDER
            </Badge>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              One Platform. One Source of Truth.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stakeholders.map((stakeholder, idx) => (
              <Card key={idx} className="border-slate-200 hover:shadow-xl hover:border-[#C8D4C0] transition-all duration-300 rounded-3xl overflow-hidden">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E8EDE5] to-[#F5EDCE] rounded-2xl flex items-center justify-center text-[#4B5E40] mb-4">
                    {stakeholder.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{stakeholder.title}</h3>
                  <ul className="space-y-2">
                    {stakeholder.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Animated Steps */}
      <section id="how-it-works" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#E8EDE5] text-[#3C4C33] hover:bg-[#E8EDE5]">
              HOW IT WORKS
            </Badge>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              Create Your Budget in
              <span className="bg-gradient-to-r from-[#4B5E40] to-[#8FA385] bg-clip-text text-transparent"> 4 Simple Steps</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Steps */}
            <div className="space-y-4">
              {steps.map((step, idx) => (
                <div 
                  key={idx}
                  className={`p-6 rounded-2xl border-2 transition-all duration-500 cursor-pointer ${
                    currentStep === idx 
                      ? 'border-[#4B5E40] bg-[#F3F5F2] shadow-lg' 
                      : 'border-slate-200 bg-white hover:border-[#C8D4C0]'
                  }`}
                  onClick={() => setCurrentStep(idx)}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg transition-colors ${
                      currentStep === idx 
                        ? 'bg-[#4B5E40] text-white' 
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-1 ${currentStep === idx ? 'text-[#2A3525]' : 'text-slate-900'}`}>
                        {step.title}
                      </h3>
                      <p className={currentStep === idx ? 'text-[#3C4C33]' : 'text-slate-600'}>
                        {step.description}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${currentStep === idx ? 'text-[#4B5E40] translate-x-1' : 'text-slate-300'}`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Animated Preview */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 shadow-2xl">
              <div className="bg-slate-800/80 rounded-2xl p-5 min-h-[350px] flex items-center justify-center">
                {currentStep === 0 && (
                  <div className="text-center animate-fade-in">
                    <Building className="w-16 h-16 text-[#8FA385] mx-auto mb-4" />
                    <p className="text-white text-lg font-semibold">Add Property Details</p>
                    <p className="text-slate-400">50 rooms • Sydney • Hotel</p>
                  </div>
                )}
                {currentStep === 1 && (
                  <div className="w-full animate-fade-in">
                    <p className="text-slate-400 text-sm mb-4">Monthly Occupancy & ADR</p>
                    <div className="grid grid-cols-6 gap-2">
                      {[65, 72, 78, 82, 85, 80, 75, 70, 68, 74, 79, 85].map((val, i) => (
                        <div key={i} className="text-center">
                          <div className="h-20 bg-gradient-to-t from-[#C9A227]/50 to-transparent rounded" style={{ height: `${val}%` }} />
                          <p className="text-xs text-slate-500 mt-1">{['J','F','M','A','M','J','J','A','S','O','N','D'][i]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {currentStep === 2 && (
                  <div className="w-full animate-fade-in">
                    <p className="text-slate-400 text-sm mb-4">Expense Categories</p>
                    <div className="space-y-2">
                      {["Housekeeping - $15/room", "Reception - 6% revenue", "Maintenance - $2,500/mo", "Marketing - 3% revenue"].map((exp, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-2">
                          <span className="text-slate-300 text-sm">{exp}</span>
                          <Check className="w-4 h-4 text-emerald-400" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {currentStep === 3 && (
                  <div className="text-center animate-fade-in">
                    <Download className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                    <p className="text-white text-lg font-semibold">Reports Ready!</p>
                    <p className="text-slate-400">P&L • Cash Flow • 5-Year Projections</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-100 text-amber-700 hover:bg-amber-100">
              SIMPLE PRICING
            </Badge>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
              Choose Your Plan
            </h2>
            <p className="text-lg text-slate-600">
              Start with a 7-day trial or unlock full power with our Professional plan.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Starter Plan */}
            <Card className="border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Starter</h3>
                <p className="text-slate-600 mb-6">Perfect to try the platform</p>
                <div className="mb-6">
                  <span className="text-5xl font-extrabold text-slate-900">$30</span>
                  <span className="text-slate-500 ml-2">/ 7 days</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    "Unlimited Properties",
                    "Unlimited Budgets",
                    "Full Budget Creation",
                    "P&L Reports",
                    "5-Year Projections",
                    "Unlimited Downloads",
                    "7 Days Access"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <Check className="w-5 h-5 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full py-6 text-lg rounded-xl border-2 border-slate-900 bg-white text-slate-900 hover:bg-slate-50 font-semibold">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Professional Plan */}
            <Card className="border-2 border-[#4B5E40] rounded-3xl overflow-hidden shadow-xl relative">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-[#4B5E40] to-[#8FA385] text-white px-4 py-1 rounded-bl-xl text-sm font-semibold">
                BEST VALUE
              </div>
              <CardContent className="p-8 bg-gradient-to-b from-[#F5EDCE] to-white">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Professional</h3>
                <p className="text-slate-600 mb-6">For serious hotel operators</p>
                <div className="mb-6">
                  <span className="text-5xl font-extrabold text-slate-900">$120</span>
                  <span className="text-slate-500 ml-2">/ year</span>
                  <p className="text-emerald-600 text-sm mt-1">That's only $10/month!</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    "Unlimited Properties",
                    "Unlimited Budgets",
                    "Full Budget Creation",
                    "All 5 Report Types",
                    "Unlimited Downloads",
                    "Custom Scenarios (3)",
                    "Budget vs Actuals Tracking",
                    "Priority Support",
                    "12 Months Access"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-[#4B5E40]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full py-6 text-lg rounded-xl bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white font-semibold shadow-lg">
                    Start Professional
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#8B7355] via-[#C9A227] to-[#D4B030] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
            Get Your Free Hotel Budget Template
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Join thousands of hotel managers using Australia's #1 hotel budget template. Create professional budgets with P&L reports and cash flow forecasts in minutes.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-[#2A3525] hover:bg-slate-100 text-lg px-10 py-7 rounded-full font-bold shadow-xl"
            >
              Download Free Hotel Budget Template
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex flex-wrap items-center justify-center gap-8 mt-12 text-white">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              <span>Instant Excel Download</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>500+ Hotels Trust Us</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section - What is a Hotel Budget Template */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
            What is a Hotel Budget Template?
          </h2>
          <div className="prose prose-lg text-slate-600 max-w-none">
            <p className="mb-4">
              A <strong>hotel budget template</strong> is an essential financial planning tool designed specifically for hospitality businesses. It helps hotel managers, motel owners, and accommodation providers create accurate annual budgets, forecast revenue, track expenses, and generate professional financial reports.
            </p>
            <p className="mb-4">
              HotelBudget's free <strong>hotel budget template</strong> goes beyond traditional spreadsheets by offering automatic calculations, industry-standard expense benchmarks, and instant Excel export. Whether you're running a boutique hotel, motel, serviced apartment, or holiday park, our <strong>hotel budget spreadsheet</strong> adapts to your needs.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">Key Features of Our Hotel Budget Template:</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>5-Year Financial Projections</strong> - Plan ahead with multi-year forecasting</li>
              <li><strong>Profit & Loss Reports</strong> - Professional P&L statements ready for banks</li>
              <li><strong>Cash Flow Forecasting</strong> - Track monthly cash flow with precision</li>
              <li><strong>Expense Tracking</strong> - Monitor all hotel operating expenses</li>
              <li><strong>Revenue Analysis</strong> - Understand your occupancy and ADR impact</li>
              <li><strong>Excel Download</strong> - Export your hotel budget template to Excel instantly</li>
            </ul>
            <p>
              Whether you're preparing a <strong>motel budget template</strong> for a small property or need a comprehensive <strong>hotel financial planning</strong> tool for a larger operation, HotelBudget provides the flexibility and professional output you need. Our <strong>accommodation budget template</strong> is trusted by hotel managers across Australia.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#4B5E40] to-[#8FA385] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
                HotelBudget
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/login" className="text-slate-400 hover:text-white transition-colors">Sign In</Link>
              <Link to="/register" className="text-slate-400 hover:text-white transition-colors">Get Started</Link>
              <Link to="/pricing" className="text-slate-400 hover:text-white transition-colors">Pricing</Link>
            </div>
            <p className="text-slate-500 text-sm">
              © 2024 HotelBudget - Free Hotel Budget Template Australia. All rights reserved.
            </p>
          </div>
          {/* SEO Footer Links */}
          <div className="mt-8 pt-8 border-t border-slate-800">
            <p className="text-slate-500 text-sm text-center">
              Hotel Budget Template | Motel Budget Template | Hotel Budget Spreadsheet | Hotel Financial Planning | Hotel P&L Template | Cash Flow Forecasting | Accommodation Budget Template Australia
            </p>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
